// The review values as the Swift encodes them (SuiteSync/Review.swift `ReviewCopy`, `ReviewNote`; SuiteModel/ReaderRound.swift
// `ReaderProgress`), with their JSON (`JSONEncoder.review`: sorted keys, dates as seconds since 2001). No Automerge: the
// reader page imports this (`@writing-suite/core/review/values`). Web links add two optional fields to the copy that the
// Swift's decoder ignores: `reader` (the name the writer gave this link's reader) and `notesBy` (the round's date).
import {
  boolean, fromReferenceSeconds, number, object, optionalString, referenceDate, ReviewDataError, sortedJSON, string, toReferenceSeconds, whole,
} from './json.ts';

export interface ReviewCopySection {
  fragmentID: string;
  title: string;
  /** The fragment's stored text at the checkpoint (the prose dialect); reviewers' anchors are scalar offsets into it. */
  text: string;
  depth: number;
  isFolder: boolean;
}

export interface ReviewCopy {
  id: string;
  projectID: string;
  projectTitle: string;
  checkpointID: string;
  checkpointName: string;
  from: string;
  message: string;
  createdAt: Date;
  sections: ReviewCopySection[];
  /** Web links: the reader this link was made for. */
  reader?: string;
  /** Web links: when the writer would like notes by. */
  notesBy?: Date;
}

/** `ReviewCopy.title`. */
export const reviewCopyTitle = (copy: Pick<ReviewCopy, 'projectTitle' | 'checkpointName'>) => `${copy.projectTitle} — ${copy.checkpointName}`;

export interface ReviewNote {
  id: string;
  fragmentID: string;
  anchorStart: number;
  anchorEnd: number;
  quote: string;
  body: string;
  author: string;
  createdAt: Date;
}

export interface ReaderProgress {
  reviewID: string;
  /** The name the reader gave, for display. */
  reader: string;
  /** Who sent it, as the writer's app knows them (web links: the round's reader id). */
  readerID?: string;
  furthestFragmentID: string;
  /** How far through the copy (0–1). */
  fraction: number;
  updatedAt: Date;
  withdrawn?: boolean;
}

// JSON (`JSONEncoder.review`) ---------------------------------------------------------------------------------------

export const encodeReviewCopy = (copy: ReviewCopy): string => sortedJSON(copy, toReferenceSeconds);
export const encodeReviewNote = (note: ReviewNote): string => sortedJSON(note, toReferenceSeconds);
export const encodeReaderProgress = (p: ReaderProgress): string => sortedJSON(p, toReferenceSeconds);

export function decodeReviewCopy(json: string): ReviewCopy {
  const o = object(JSON.parse(json), 'The copy');
  const sections = o.sections;
  if (!Array.isArray(sections)) throw new ReviewDataError('sections isn’t a list');
  const notesBy = o.notesBy;
  return {
    id: string(o, 'id'),
    projectID: string(o, 'projectID'),
    projectTitle: string(o, 'projectTitle'),
    checkpointID: string(o, 'checkpointID'),
    checkpointName: string(o, 'checkpointName'),
    from: string(o, 'from'),
    message: string(o, 'message'),
    createdAt: referenceDate(o, 'createdAt'),
    sections: sections.map((s) => {
      const x = object(s, 'A section');
      return { fragmentID: string(x, 'fragmentID'), title: string(x, 'title'), text: string(x, 'text'), depth: whole(x, 'depth'), isFolder: boolean(x, 'isFolder') };
    }),
    ...(optionalString(o, 'reader') ? { reader: optionalString(o, 'reader') } : {}),
    ...(typeof notesBy === 'number' && Number.isFinite(notesBy) ? { notesBy: fromReferenceSeconds(notesBy) } : {}),
  };
}

export function decodeReviewNote(json: string): ReviewNote {
  const o = object(JSON.parse(json), 'The note');
  return {
    id: string(o, 'id'),
    fragmentID: string(o, 'fragmentID'),
    anchorStart: whole(o, 'anchorStart'),
    anchorEnd: whole(o, 'anchorEnd'),
    quote: string(o, 'quote'),
    body: string(o, 'body'),
    author: string(o, 'author'),
    createdAt: referenceDate(o, 'createdAt'),
  };
}

/** `ReaderProgress.init(from:)`: a fraction that isn't a finite number is refused, one outside 0–1 brought inside it. */
export function decodeReaderProgress(json: string, dates: 'reference' | 'iso8601' = 'reference'): ReaderProgress {
  const o = object(JSON.parse(json), 'The progress');
  const fraction = number(o, 'fraction');
  const updatedAt = dates === 'reference' ? referenceDate(o, 'updatedAt') : isoDate(o.updatedAt);
  const withdrawn = o.withdrawn === undefined || o.withdrawn === null ? undefined : boolean(o, 'withdrawn');
  const readerID = optionalString(o, 'readerID');
  return {
    reviewID: string(o, 'reviewID'),
    reader: string(o, 'reader'),
    ...(readerID !== undefined ? { readerID } : {}),
    furthestFragmentID: string(o, 'furthestFragmentID'),
    fraction: Math.min(1, Math.max(0, fraction)),
    updatedAt,
    ...(withdrawn !== undefined ? { withdrawn } : {}),
  };
}

function isoDate(v: unknown): Date {
  if (typeof v !== 'string') throw new ReviewDataError('updatedAt isn’t a date');
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) throw new ReviewDataError('updatedAt isn’t a date');
  return d;
}
